import * as React from 'react';
import { Provider } from 'react-redux'
import { createRoot } from 'react-dom/client';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { PersistGate } from 'redux-persist/integration/react'
import { connectToTicker } from './indoor.socket';
import { BrowserRouter } from 'react-router-dom';
import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';
import 'normalize.css/normalize.css';

import { associationTickerStateReducer } from './ticker-state/reducer';
import { dayFilterReducer } from './day-filter/reducer';
import { connectionReducer } from './connection/reducer';
import { favoritesReducer } from './match/favourite/reducer';
import { viewSelectionReducer } from './view/view-selection/reducer';
import { matchDetailViewReducer } from './view/match-detail/detail-view/reducer';
import './index.scss';
import App from './App';
import { filterNavReducer } from './filter-nav/reducer';
import { beachTickerStateReducer } from './beach/reducer';
import { connectToBeachTicker } from './beach/beach.socket';

export interface AppProps {
  tickerId: string
  teamId?: string
  beach: boolean
  headTitle: string
  appTitle: string
  footerText: string
  footerUrl: string
  adBannerUrl: string
}

const ticker = document.getElementById('sams-ticker') as HTMLElement

const appProps: AppProps = {
  tickerId: ticker.getAttribute('tickerId') as string,
  beach: ticker.getAttribute('beach') as string === 'true',
  teamId: ticker.getAttribute('teamId') as string,
  headTitle: ticker.getAttribute('headTitle') as string,
  appTitle: ticker.getAttribute('appTitle') as string,
  footerText: ticker.getAttribute('footerText') as string,
  footerUrl: ticker.getAttribute('footerUrl') as string,
  adBannerUrl: ticker.getAttribute('adBannerUrl') as string
}

const persistConfig = {
  key: 'root',
  storage,
  blacklist: appProps.teamId
    ? ['tickerState', 'connection', 'router']
    : ['dayFilter', 'tickerState', 'connection', 'router', 'matchDetailView']
}

export const associationReducer = combineReducers({
  connection: connectionReducer,
  favorites: favoritesReducer,
  tickerState: associationTickerStateReducer,
  dayFilter: dayFilterReducer,
  filterNav: filterNavReducer,
  matchDetailView: matchDetailViewReducer,
  selectedView: viewSelectionReducer
})

export const beachReducer = combineReducers({
  connection: connectionReducer,
  favorites: favoritesReducer,
  tickerState: beachTickerStateReducer,
  dayFilter: dayFilterReducer,
  filterNav: filterNavReducer,
  selectedView: viewSelectionReducer
})

export const reducer = appProps.beach ? beachReducer : associationReducer

export type AssociationRootState = ReturnType<typeof associationReducer>
export type BeachRootState = ReturnType<typeof beachReducer>
export type TickerRootState = AssociationRootState | BeachRootState

const persistedReducer = persistReducer(persistConfig, reducer as any)

const store = process.env.NODE_ENV === 'production'
  ? configureStore({
    reducer: persistedReducer, devTools: false,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
  })

  : configureStore({
    reducer: persistedReducer, devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
  })
const persistor = persistStore(store)

export type TickerStore = typeof store

if (appProps.tickerId) {
  if (appProps.beach) {
    connectToBeachTicker(appProps.tickerId, store)
  } else {
    connectToTicker(appProps.tickerId, store, appProps.teamId)
  }
}

const root = createRoot(ticker)

root.render(
  appProps.tickerId ?
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter>
          <App appProps={appProps}></App>
        </BrowserRouter>
      </PersistGate>
    </Provider>
    :
    <h1>Bitte ergänzen Sie das html element mit der id <b>sams-ticker</b> mit den Attributen <b>tickerId</b> und <b>tickerType</b></h1>
)
